import type { AppErrorCode } from '@/net/errors';
import { tr, type Dictionary } from './tr';

export { tr };
export type { Dictionary };

/** Dotted paths of every string leaf in the dictionary, e.g. `'auction.yourTurn'`. */
type Leaves<T, P extends string = ''> = {
  [K in keyof T & string]: T[K] extends string
    ? `${P}${K}`
    : T[K] extends readonly unknown[]
      ? never
      : T[K] extends object
        ? Leaves<T[K], `${P}${K}.`>
        : never;
}[keyof T & string];

export type TKey = Leaves<Dictionary>;
export type TParams = Record<string, string | number>;

/** Replace `{name}` placeholders. Unknown placeholders are left as they are. */
export function format(template: string, params?: TParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (m, k: string) => {
    const v = params[k];
    return v === undefined ? m : String(v);
  });
}

function lookup(key: string): string | undefined {
  let node: unknown = tr;
  for (const part of key.split('.')) {
    if (node === null || typeof node !== 'object') return undefined;
    node = (node as Record<string, unknown>)[part];
  }
  return typeof node === 'string' ? node : undefined;
}

/** Translate a key (type-checked) and fill placeholders. Missing keys render as the key itself. */
export function t(key: TKey, params?: TParams): string {
  return format(lookup(key) ?? key, params);
}

/** Turkish-aware uppercase: `istanbul` → `İSTANBUL`, `ışık` → `IŞIK`. */
export function upperTr(s: string): string {
  return s.toLocaleUpperCase('tr');
}

/** Turkish-aware lowercase: `İSTANBUL` → `istanbul`. */
export function lowerTr(s: string): string {
  return s.toLocaleLowerCase('tr');
}

/** Friendly Turkish message for any error code (unknown codes get a generic message). */
export function errorMessage(code: string | null | undefined): string {
  if (!code) return tr.errors.UNKNOWN;
  return (tr.errors as Record<string, string>)[code] ?? tr.errors.UNKNOWN;
}

export function isKnownErrorCode(code: string): code is AppErrorCode {
  return Object.hasOwn(tr.errors, code);
}
